import { useState, useEffect, useRef } from 'react'
import { isPrint } from '@/utils/print'
import { cx } from '@/utils/strings'
import { useIntersectionObserver } from 'usehooks-ts'
import ShieldLoader from './shield-loader'
import VimeoChapters from './vimeo-chapters'
import RichText from '@/components/ui/rich-text'
import { useTranslations } from '@/utils/locale'
import { TypeComponentRichText } from '@/types/ctf'

// videos supported:
// vimeo example     https://player.vimeo.com/video/632391847
// youtube example   https://www.youtube.com/embed/YhIwT4AMvm0

interface VideoProps {
  className?: string
  readonly fields: {
    readonly url: string
    readonly aspectRatio?: string
    readonly showChapters?: boolean
    readonly chaptersIntro?: TypeComponentRichText<'WITHOUT_UNRESOLVABLE_LINKS'>
  }
}

export default function Video({ className, fields }: VideoProps) {
  const { url, aspectRatio, showChapters = false, chaptersIntro } = fields
  const [isVimeo, setIsVimeo] = useState(false)
  const scriptRef = useRef<HTMLScriptElement | null>(null)
  const [vimeoLoaded, setVimeoLoaded] = useState(typeof window !== 'undefined' && !!window.Vimeo)

  const { t } = useTranslations()

  // default params dont work because stinker contentful sends null instead of undefined
  const aspectRatioWithDefault = aspectRatio || '16 / 9'

  const isprint = isPrint()

  const { isIntersecting, ref: videoContainer } = useIntersectionObserver({ freezeOnceVisible: true })

  useEffect(() => {
    // Only proceed if it's a Vimeo video, we're in browser context, and the video is intersecting
    if (!url?.toLowerCase().includes('vimeo') || typeof window === 'undefined' || !isIntersecting) {
      return
    }

    // Check if Vimeo SDK is already loaded
    if (window.Vimeo) {
      setVimeoLoaded(true)
      return
    }

    if (!document.querySelector('script#vimeo-player-sdk')) {
      const script = document.createElement('script')
      script.id = 'vimeo-player-sdk'
      script.src = 'https://player.vimeo.com/api/player-2.26.0.js'
      script.integrity = 'sha384-cspqYTlnJGgea0pMlbvC1pz6dOPp5NrryEqRF400Iiukq7L+BeYZenBZ+9Ee9I4F'
      script.crossOrigin = 'anonymous'
      script.async = true

      script.onload = () => {
        setVimeoLoaded(true)
      }

      document.head.appendChild(script)
      scriptRef.current = script
    }
  }, [url, showChapters, isIntersecting])

  // Extract video ID from URL
  useEffect(() => {
    if (url?.toLowerCase().includes('vimeo')) {
      setIsVimeo(true)
    }
  }, [url])

  const extractVideoUrl = (url: string) => {
    const youtubeRegex = /youtube\.com\/embed\/([\w-]{11})/
    const youtubeMatch = youtubeRegex.exec(url)
    if (youtubeMatch) return `https://img.youtube.com/vi/${youtubeMatch[1]}/hqdefault.jpg`

    const vimeoRegex = /vimeo\.com\/video\/(\d+)/
    const vimeoMatch = vimeoRegex.exec(url)
    if (vimeoMatch) return `https://vumbnail.com/${vimeoMatch[1]}.jpg`

    return ''
  }

  if (isprint) {
    return (
      <div className="flex justify-center">
        <img src={extractVideoUrl(url)} alt="Video thumbnail" className="max-w-full" />
      </div>
    )
  }

  return (
    <div className={cx('video-container', className)}>
      <div
        className={cx(
          'w-full',
          aspectRatioWithDefault === '16 / 9' && `aspect-16/9`,
          aspectRatioWithDefault === '9 / 16' && `aspect-9/16`,
          aspectRatioWithDefault === '4 / 3' && `aspect-4/3`,
          aspectRatioWithDefault === '1 / 1' && `aspect-1/1`
        )}
        data-testid="videoplayer"
        ref={videoContainer}
      >
        {isIntersecting ? (
          <>
            {/* Player ratio: 100 / (1280 / 720) */}
            {url?.toLowerCase()?.includes('vimeo') && (
              <iframe
                className="w-full h-full"
                src={`${url}?title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479`}
                title="Bitwarden video"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                sandbox="allow-scripts allow-same-origin allow-popups"
              />
            )}
            {url?.toLowerCase()?.includes('youtube') && (
              <iframe
                className="w-full h-full"
                src={`${url}?rel=0&modestbranding=1`}
                title="Bitwarden video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                sandbox="allow-scripts allow-same-origin allow-popups"
              />
            )}
          </>
        ) : (
          <div className="flex items-center justify-center w-full h-full">
            <ShieldLoader className="w-auto" />
          </div>
        )}
      </div>

      {showChapters && isVimeo && (
        <div className="mt-4">
          {chaptersIntro ? (
            <RichText body={chaptersIntro} />
          ) : (
            <h3 className="text-lg font-medium mb-2">{t('Video Chapters')}</h3>
          )}
        </div>
      )}
      {/* Show chapters if enabled and it's a Vimeo video */}
      {showChapters && isVimeo && isIntersecting && (
        <div className="mt-4">
          <VimeoChapters videoUrl={url} vimeoLoaded={vimeoLoaded} />
        </div>
      )}
    </div>
  )
}
